/**
 * useRequests Hook
 * ================
 * All request state management in one place.
 * Combines React Query (for caching) with Firestore real-time listeners
 * so the inbox updates live when a lender approves a request.
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createRequest,
  approveRequest,
  cancelRequest,
  requestExtension,
  markReturned,
  subscribeToRequests,
  type CreateRequestPayload,
} from "@/features/requests/requestsService";
import { submitReview } from "@/features/reviews/reviewsService";
import {
  subscribeToUnreadCount,
  subscribeToNotifications,
  markNotificationRead,
  markAllRead,
} from "@/features/notifications/notificationsService";
import { bookKeys } from "@/hooks/useBooks";
import type { BorrowRequest, Notification } from "@/types";

// ─── QUERY KEYS ──────────────────────────────────────────────────────────────
export const requestKeys = {
  all:       ["requests"]                                          as const,
  incoming:  (uid: string) => ["requests", "incoming", uid]       as const,
  myBorrows: (uid: string) => ["requests", "borrows",  uid]       as const,
};

// ─── useLiveRequests — real-time listener ─────────────────────────────────────
/**
 * Returns live-updating list of requests, subscribed via onSnapshot.
 * No stale data — updates instantly when lender approves/declines.
 */
export function useLiveRequests(
  userId: string | null,
  role:   "borrower" | "lender"
) {
  const [requests, setRequests] = useState<BorrowRequest[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    const unsub = subscribeToRequests(userId, role, (data) => {
      setRequests(data);
      setLoading(false);
    });
    return unsub;
  }, [userId, role]);

  // Derived slices
  const pending  = requests.filter(r => r.status === "pending");
  const active   = requests.filter(r => ["approved","active","extended","overdue"].includes(r.status));
  const history  = requests.filter(r => ["returned","cancelled"].includes(r.status));

  return { requests, pending, active, history, loading };
}

// ─── useCreateRequest ─────────────────────────────────────────────────────────
export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRequestPayload) => createRequest(payload),
    onSuccess: (result, vars) => {
      if (!result.ok) return;
      // Invalidate the book detail (isAvailable may have changed after approval)
      qc.invalidateQueries({ queryKey: bookKeys.detail(vars.bookId) });
    },
  });
}

// ─── useApproveRequest ────────────────────────────────────────────────────────
export function useApproveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => approveRequest(requestId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookKeys.lists() });
    },
  });
}

// ─── useCancelRequest ─────────────────────────────────────────────────────────
export function useCancelRequest() {
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      cancelRequest(id, reason),
  });
}

// ─── useExtendRequest ─────────────────────────────────────────────────────────
export function useExtendRequest() {
  return useMutation({
    mutationFn: (requestId: string) => requestExtension(requestId),
  });
}

// ─── useMarkReturned ─────────────────────────────────────────────────────────
export function useMarkReturned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => markReturned(requestId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookKeys.lists() });
    },
  });
}

// ─── useSubmitReview ─────────────────────────────────────────────────────────
export function useSubmitReview() {
  return useMutation({
    mutationFn: submitReview,
  });
}

// ─── useNotifications — real-time ────────────────────────────────────────────
export function useNotifications(userId: string | null) {
  const [notifs,      setNotifs]      = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!userId) return;
    const u1 = subscribeToNotifications(userId, setNotifs);
    const u2 = subscribeToUnreadCount(userId, setUnreadCount);
    return () => { u1(); u2(); };
  }, [userId]);

  return {
    notifications: notifs,
    unreadCount,
    markRead:    (id: string) => markNotificationRead(id),
    markAllRead: ()           => markAllRead(userId ?? ""),
  };
}
